import React, { useContext, useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getPacket } from '../../../services/PacketService';
import { PacketContext } from './Context';

// Re-export constants and utilities for backward compatibility
export { QUESTION_TYPES, LOADING_BLOCK } from './packetContextUtils';
export {
  removeCitationsFromReading,
  getDeterministicShuffle,
  useRandomizedIndices,
} from './packetContextUtils';

export const usePacketContext = () => {
  const context = useContext(PacketContext);
  if (!context) {
    throw new Error('usePacketContext must be used within a PacketProvider');
  }
  return context;
};

export const PacketProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const { packetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Initial load from API
  useEffect(() => {
    if (!packetId) return;

    const loadPacket = async () => {
      try {
        const response = await getPacket(packetId);

        // Check for project redirect — legacy packets are auto-migrated to projects
        // and the API returns redirect information
        const projectRedirect = response.data.project_redirect;
        if (projectRedirect && projectRedirect.redirect && projectRedirect.url) {
          // Redirect to the project view
          navigate(`${projectRedirect.url}${location.search}`, { replace: true });
        } else {
          console.error('Failed to load project for packet');
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load packet:', error);
        setIsLoading(false);
      }
    };
    loadPacket();
  }, [packetId, navigate]);

  const value = useMemo(
    () => ({
      isLoading,
    }),
    [isLoading]
  );

  return <PacketContext.Provider value={value}>{children}</PacketContext.Provider>;
};
